import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";

import { defaultCollectionPoint, defaultContainers } from "../consts/collectionPoint";
import {
  COLLECTION_POINTS_PAGE_PATH,
  DELETE_INVITE_TO_MOBILE_APP_URL,
  GET_COLLECTION_POINT_DETAIL_URL,
  POST_INVITE_TO_MOBILE_APP_URL,
  PUT_COLLECTION_POINT_URL,
  UPDATE_IS_PAID_STATUS_URL,
} from "../middleware/routes";
import { RootState } from "../store";
import { UpdateBillingPaymentScheduleDate } from "../types/billing";
import { BillingPaymentScheduleType } from "../types/billings";
import {
  ICollectionPoint,
  IInviteToMobileAppResponse,
  InvitationStatus,
  IUpdateIsPaidStatusRequest,
  IUpdateLocationIsPaidStatusResponse,
  PaymentStatus,
  PutCollectionPointPaymentArgs,
  PutCollectionPointRefundArgs,
} from "../types/collectionPoint";
import { IContainer } from "../types/container";
import { IPagination } from "../types/pagination";
import { ICollectionPointState, Status } from "../types/state";
import { axiosRequestApi } from "../utils/axiosRequest";
import { filterUniqueContainersById, sortContainersByDisableTime } from "../utils/containers";
import { getSkeletonNotMockedArray } from "../utils/tables/getSkeletonNotMockedArray";
import { getSkeletonSuccessArray } from "../utils/tables/getSkeletonSuccessArray";
import { defaultPagination } from "./billingLocationsReducer";
import {
  adaptCollectionPointDetailResponseForFrontend,
  adaptCollectionPointForBackend,
  adaptCollectionPointPaymentForBackend,
  adaptCollectionPointRefundForBackend,
} from "./utils/collectionPointAdapt";

const initCollectionPointState: ICollectionPointState = {
  status: Status.idle,
  collectionPoint: defaultCollectionPoint,
  pagination: defaultPagination,
  requestId: "",
  isNextPageEnable: false,
  scrollTop: 0,
  isUpdating: false,
};

export const collectionPointSlice = createSlice({
  name: "collectionPoint",
  initialState: initCollectionPointState,
  reducers: {
    cleanUpCollectionPointDetail: (state: ICollectionPointState) => {
      state.status = Status.idle;
      state.collectionPoint = structuredClone(defaultCollectionPoint);
      state.pagination = structuredClone(defaultPagination);
    },
    cleanUpCollectionPointDetailContainers: (state: ICollectionPointState) => {
      state.status = Status.idle;
      state.collectionPoint.containers = structuredClone(defaultContainers);
      state.pagination = structuredClone(defaultPagination);
    },
    setCollectionPoint: (state: ICollectionPointState, action: PayloadAction<Partial<ICollectionPoint>>) => {
      const newCollectionPoint = { ...state.collectionPoint, ...action.payload };

      state.collectionPoint = newCollectionPoint;
    },
    setCollectionPointDetailPagination: (state: ICollectionPointState, action: PayloadAction<IPagination>) => {
      state.pagination = action.payload;
    },
    setCollectionPointContainers: (state: ICollectionPointState, action: PayloadAction<IContainer[]>) => {
      const oldContainers = getSkeletonNotMockedArray([...state.collectionPoint.containers]);
      const newContainers = [...oldContainers, ...action.payload];

      state.collectionPoint.containers = newContainers;
    },
    setCollectionPointDetailScrollTop: (state: ICollectionPointState, action: PayloadAction<number>) => {
      state.scrollTop = action.payload;
    },
    updateBillingPaymentScheduleType: (
      state: ICollectionPointState,
      action: PayloadAction<BillingPaymentScheduleType>
    ) => {
      if (state.collectionPoint.paymentSchedule) {
        state.collectionPoint.paymentSchedule.type = action.payload;
      }
    },
    updateCollectionPointBillingPaymentDate: (
      state: ICollectionPointState,
      action: PayloadAction<UpdateBillingPaymentScheduleDate>
    ) => {
      const { field, value } = action.payload;
      if (state.collectionPoint.paymentSchedule) {
        state.collectionPoint.paymentSchedule[field] = value;
      }
    },
    resetCollectionPointState: () => {
      return initCollectionPointState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateLocationIsPaidStatusThunk.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) {
          return;
        }

        state.status = Status.success;
        state.isUpdating = false;

        state.collectionPoint.isPaid = action.payload.Status === PaymentStatus.Paid;
      })
      .addCase(getCollectionPointDetailThunk.pending, (state, action) => {
        state.status = Status.requesting;
        state.requestId = action.meta.requestId;
      })
      .addCase(getCollectionPointDetailThunk.rejected, (state, action) => {
        state.status = Status.error;
        if (action.payload === 404) {
          window.location.href = COLLECTION_POINTS_PAGE_PATH;
        }
      })
      .addCase(inviteToMobileAppThunk.fulfilled, (state, action) => {
        if (!action.payload.token) return;
        state.status = Status.success;
        state.collectionPoint.invitationToken = action.payload.token;
        state.collectionPoint.invitationStatus = InvitationStatus.Invited;
        state.isUpdating = false;
      })
      .addMatcher(
        isAnyOf(
          inviteToMobileAppThunk.pending,
          putCollectionPointThunk.pending,
          putCollectionPointPaymentThunk.pending,
          putCollectionPointRefundThunk.pending,
          updateLocationIsPaidStatusThunk.pending
        ),
        (state, action) => {
          state.status = Status.requesting;
          state.requestId = action.meta.requestId;
          state.isUpdating = true;
        }
      )
      .addMatcher(
        isAnyOf(
          getCollectionPointDetailThunk.fulfilled,
          putCollectionPointThunk.fulfilled,
          putCollectionPointPaymentThunk.fulfilled,
          putCollectionPointRefundThunk.fulfilled
        ),
        (state, action) => {
          if (state.requestId !== action.meta.requestId) {
            return;
          }

          state.status = Status.success;
          state.isUpdating = false;

          const containersFromState = state.collectionPoint.containers;
          const collectionPointFromResponse = action.payload;

          if (!collectionPointFromResponse) {
            window.location.href = COLLECTION_POINTS_PAGE_PATH;
          }

          const containersFromResponse = sortContainersByDisableTime(
            collectionPointFromResponse.containers,
            Number(action.payload.year)
          );

          const mergedContainers = getSkeletonSuccessArray(containersFromState, containersFromResponse);
          const newContainers = filterUniqueContainersById(mergedContainers);

          const newCollectionPoint = { ...collectionPointFromResponse, containers: newContainers };
          state.collectionPoint = { ...state.collectionPoint, ...newCollectionPoint };
        }
      )
      .addMatcher(
        isAnyOf(
          putCollectionPointThunk.rejected,
          putCollectionPointPaymentThunk.rejected,
          inviteToMobileAppThunk.rejected,
          putCollectionPointRefundThunk.rejected,
          updateLocationIsPaidStatusThunk.rejected
        ),
        (state, action) => {
          if (state.requestId !== action.meta.requestId) {
            return;
          }
          state.isUpdating = false;
          state.status = Status.error;
          state.isUpdating = false;
        }
      );
  },
});

export const {
  cleanUpCollectionPointDetail,
  setCollectionPoint,
  setCollectionPointDetailPagination,
  setCollectionPointContainers,
  resetCollectionPointState,
  setCollectionPointDetailScrollTop,
  updateBillingPaymentScheduleType,
  updateCollectionPointBillingPaymentDate,
  cleanUpCollectionPointDetailContainers,
} = collectionPointSlice.actions;

export const collectionPointReducer = collectionPointSlice.reducer;

export const getCollectionPointDetailThunk = createAsyncThunk<ICollectionPoint, number, { state: RootState }>(
  "collectionPoint/getCollectionPointDetailThunk",
  async (collectionPointId, { getState, rejectWithValue }) => {
    const state: RootState = getState();
    const requestBody = {
      locationId: collectionPointId,
      year: state.application.years.selectedYear.value,
      pagination: state.collectionPoint.pagination,
    };

    try {
      const response = await axiosRequestApi.post(GET_COLLECTION_POINT_DETAIL_URL, requestBody);
      return adaptCollectionPointDetailResponseForFrontend(response.data);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const message = axiosError?.response?.data?.message;
      return rejectWithValue(message);
    }
  }
);
export const inviteToMobileAppThunk = createAsyncThunk<
  IInviteToMobileAppResponse,
  string,
  { state: RootState; rejectValue: string }
>("collectionPoint/inviteToMobileAppThunk", async (email, { getState }) => {
  const state = getState();
  const collectionPointId = state.collectionPoint.collectionPoint.locationId;

  const requestBody = {
    locationId: collectionPointId,
    email,
  };

  const response = await axiosRequestApi.post(POST_INVITE_TO_MOBILE_APP_URL, requestBody);
  return response.data;
});

export const deleteInviteToMobileAppThunk = createAsyncThunk<void, number, { state: RootState }>(
  "collectionPoint/deleteInviteToMobileAppThunk",
  async (locationId) => {
    const response = await axiosRequestApi.delete(DELETE_INVITE_TO_MOBILE_APP_URL + `?locationId=${locationId}`);
    return response.data;
  }
);

export const putCollectionPointThunk = createAsyncThunk<ICollectionPoint, void, { state: RootState }>(
  "collectionPoint/putCollectionPointThunk",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const data = state.collectionPoint.collectionPoint;

      const adaptedData = adaptCollectionPointForBackend(data);
      const response = await axiosRequestApi.put(`${PUT_COLLECTION_POINT_URL}/${data.locationId}`, adaptedData);

      return adaptCollectionPointDetailResponseForFrontend(response?.data);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const message = axiosError?.response?.data?.message;
      return rejectWithValue(message);
    }
  }
);

export const putCollectionPointPaymentThunk = createAsyncThunk<
  ICollectionPoint,
  PutCollectionPointPaymentArgs,
  { state: RootState }
>("collectionPoint/putCollectionPointPaymentThunk", async (payment, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const collectionPoint = state.collectionPoint.collectionPoint;

    const adaptedData = adaptCollectionPointPaymentForBackend(collectionPoint, payment);
    const response = await axiosRequestApi.put(
      `${PUT_COLLECTION_POINT_URL}/${collectionPoint.locationId}`,
      adaptedData
    );
    return adaptCollectionPointDetailResponseForFrontend(response?.data);
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    return rejectWithValue(message);
  }
});

export const putCollectionPointRefundThunk = createAsyncThunk<
  ICollectionPoint,
  PutCollectionPointRefundArgs,
  { state: RootState }
>("collectionPoint/putCollectionPointRefundThunk", async (refund, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const collectionPoint = state.collectionPoint.collectionPoint;

    const adaptedData = adaptCollectionPointRefundForBackend(collectionPoint, refund);
    const response = await axiosRequestApi.put(
      `${PUT_COLLECTION_POINT_URL}/${collectionPoint.locationId}`,
      adaptedData
    );
    return adaptCollectionPointDetailResponseForFrontend(response?.data);
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    return rejectWithValue(message);
  }
});

export const updateLocationIsPaidStatusThunk = createAsyncThunk<
  IUpdateLocationIsPaidStatusResponse,
  IUpdateIsPaidStatusRequest
>("collectionPoints/updateLocationIsPaidStatusThunk", async (updateIsPaidStatusRequest: IUpdateIsPaidStatusRequest) => {
  const response = await axiosRequestApi.post(UPDATE_IS_PAID_STATUS_URL, updateIsPaidStatusRequest);
  return response.data;
});
